import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/web-sdk';

const DD_APPLICATION_ID = process.env.REACT_APP_DD_APPLICATION_ID || process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
const DD_CLIENT_TOKEN = process.env.REACT_APP_DD_CLIENT_TOKEN || process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN;
const APPLICATION_ENV = process.env.REACT_APP_ENV || process.env.NEXT_PUBLIC_ENV;

async function hashString(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function initDDFeatureFlags(
  service: string,
  targetingKey: string,
  evalContext: Record<string, string | number>,
) {
  if (!DD_APPLICATION_ID || !DD_CLIENT_TOKEN) {
    return;
  }
  const provider = new DatadogProvider({
    site: 'datadoghq.eu',
    applicationId: DD_APPLICATION_ID as string,
    clientToken: DD_CLIENT_TOKEN as string,
    env: APPLICATION_ENV,
    service,
  });
  const context = {
    targetingKey,
    ...evalContext,
  };
  await OpenFeature.setContext(context);
  await OpenFeature.setProviderAndWait(provider);
}

export async function initCPDDFeatureFlags(
  service: string,
  {
    bookingID,
    customerId,
    email,
    securityToken,
    locale,
  }: {
    bookingID?: string;
    customerId?: string | number;
    email?: string;
    securityToken?: string;
    locale: string;
  },
) {
  let targetingKey: string;

  if (customerId) {
    targetingKey = String(customerId);
  } else if (bookingID) {
    targetingKey = bookingID;
  } else if (securityToken) {
    targetingKey = await hashString(securityToken);
  } else {
    return;
  }

  const evalContext: Record<string, string | number> = { locale };

  // Add booking_id, customer_id, and email to evaluation context if present
  // This enables targeting rules based on these attributes for smoke tests and gradual rollouts
  if (bookingID) {
    evalContext.booking_id = bookingID;
  }
  if (customerId) {
    evalContext.customer_id = String(customerId);
  }
  if (email) {
    evalContext.email = email;
  }

  await initDDFeatureFlags(service, targetingKey, evalContext);
}
